'use client';

import { useEffect } from 'react';
import posthog from 'posthog-js';
import { EVENTS } from '@attik/posthog';
import { STEP_MAP } from '../constants';
import { useScheduler } from '../SchedulerContext';

/** Main scheduler step, as named in `STEP_MAP`. */
export type SchedulerStepName =
  | 'services'
  | 'property'
  | 'schedule'
  | 'contact'
  | 'summary';

export type SchedulerScreen = {
  step: SchedulerStepName;
  /** Stable, snake_case id of the screen within the step — what funnels filter on. */
  screen: string;
  /** Optional qualifier, e.g. why the escape hatch was shown. */
  detail?: string;
};

/**
 * Sends `scheduler_step_viewed` each time a scheduler screen is shown, so drop-off
 * can be pinned to the exact screen a customer left on (the last one they viewed).
 * Pass `null` when the caller has nothing to report.
 *
 * `company_id` and the company group are attached explicitly because the first
 * screen renders before `SchedulerContext` registers them as super properties.
 */
export function useTrackSchedulerScreen(target: SchedulerScreen | null) {
  const { companyId } = useScheduler();
  const step = target?.step;
  const screen = target?.screen;
  const detail = target?.detail;

  useEffect(() => {
    if (!step || !screen) return;
    const company = companyId ? String(companyId) : undefined;
    posthog.capture(EVENTS.schedulerStepViewed, {
      step,
      step_number: STEP_MAP[step],
      screen,
      screen_detail: detail,
      company_id: company,
      ...(company ? { $groups: { company } } : {}),
    });
  }, [step, screen, detail, companyId]);
}
